import React, { useMemo, useState } from 'react'
import YouTube from 'react-youtube'
import "./dlp.css"
import { MdSearch, MdKeyboardArrowDown, MdClose } from 'react-icons/md'
import { dlpSections, LEVELS } from './DlpData'
import DlpIcon from './DlpIcon'
import DlpCardModal from './DlpCardModal'
import { formatDuration } from './dlpUtils'

const LevelPill = ({ lv }) => {
    if (!lv) return null
    return <span className={`dlp-pill dlp-lv${lv}`}>{LEVELS[lv].full}</span>
}

const DlpCards = () => {

    const [search, setSearch] = useState("")
    const [filter, setFilter] = useState("all")
    const [activeCard, setActiveCard] = useState(null)
    const [activeSection, setActiveSection] = useState(null)
    const [durations, setDurations] = useState({})

    const videoIds = useMemo(() => (
        [...new Set(dlpSections.flatMap(s => s.cards).filter(c => c.videoId).map(c => c.videoId))]
    ), [])

    const query = search.trim().toLowerCase()

    const filteredSections = useMemo(() => {
        const matchesQuery = (card) => {
            if (!query) return true
            const haystack = [card.t, card.d].join(" ").toLowerCase()
            return haystack.includes(query)
        }
        return dlpSections
            .filter(section => filter === "all" || filter === "soon" || String(section.lv) === filter)
            .map(section => ({
                ...section,
                cards: section.cards.filter(card => (filter !== "soon" || card.soon) && matchesQuery(card)),
            }))
            .filter(section => section.cards.length > 0)
    }, [query, filter])

    const totalShown = filteredSections.reduce((acc, s) => acc + s.cards.length, 0)
    const totalCards = dlpSections.reduce((acc, s) => acc + s.cards.length, 0)

    const handleOpen = (card, section) => {
        setActiveCard(card)
        setActiveSection(section)
    }
    const handleClose = () => setActiveCard(null)

    const clearSearch = () => setSearch("")

    const resetFilters = () => {
        setSearch("")
        setFilter("all")
    }

    return (
        <div className="dlp-browser">
            <div className="dlp-topbar">
                <h4>Endpoint Defence: Unified Endpoint Management</h4>
                <div className="dlp-search">
                    <MdSearch className="dlp-search-icon" />
                    <input
                        placeholder="Search modules — try USB, quarantine, device groups"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Escape' && search) { e.stopPropagation(); clearSearch() } }}
                        aria-label="Search modules"
                    />
                    {search &&
                        <button
                            type="button"
                            className="dlp-search-clear"
                            onClick={clearSearch}
                            aria-label="Clear search"
                        >
                            <MdClose />
                        </button>
                    }
                </div>
            </div>

            <div className="dlp-filterrow">
                <div className="dlp-filter-wrap">
                    <select className="dlp-filter" value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Filter by level">
                        <option value="all">All levels</option>
                        {Object.keys(LEVELS).map((lv) => (
                            <option key={lv} value={lv}>{LEVELS[lv].full}</option>
                        ))}
                        <option value="soon">Coming soon</option>
                    </select>
                    <MdKeyboardArrowDown className="dlp-filter-caret" />
                </div>
                <span className="dlp-tally">{totalShown} of {totalCards} shown</span>
            </div>

            {filteredSections.length === 0 &&
                <div className="dlp-empty">
                    <h6>{search ? `No modules match "${search}"` : "No modules in this filter"}</h6>
                    <p className="mb-0 mt-2">Try a different term, or reset your search and filter.</p>
                    <button type="button" className="dlp-empty-reset" onClick={resetFilters}>
                        Clear search &amp; filters
                    </button>
                </div>
            }

            {filteredSections.map((section) => (
                <div className="dlp-section" key={section.id}>
                    <div className="dlp-section-head">
                        <h6>{section.title}</h6>
                        <span className="dlp-section-note">{section.note}</span>
                        <span className="dlp-section-tally">{section.cards.length} {section.cards.length === 1 ? "module" : "modules"}</span>
                    </div>
                    <div className="row g-4">
                        {section.cards.map((card, cardIndex) => (
                            <div key={cardIndex} className="col-sm-12 col-md-6 col-lg-4 col-xl-3">
                                <button
                                    className={`dlp-custom-shadow ${card.soon ? "dlp-card-soon" : ""}`}
                                    onClick={() => handleOpen(card, section)}
                                >
                                    <div className="dlp-card-icon">
                                        <DlpIcon name={card.icon} color="rgb(32, 31, 31)" />
                                    </div>
                                    <div className="dlp-card-title">{card.t}</div>
                                    <div className="dlp-card-desc">{card.d}</div>
                                    <div className="dlp-card-foot">
                                        <LevelPill lv={section.lv} />
                                        {card.soon
                                            ? <span className="dlp-soon-badge">Coming soon</span>
                                            : (durations[card.videoId] && <span className="dlp-mins">{durations[card.videoId]}</span>)
                                        }
                                    </div>
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            ))}

            {activeCard &&
                <DlpCardModal card={activeCard} section={activeSection} onClose={handleClose} />
            }

            <div style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', opacity: 0, pointerEvents: 'none' }} aria-hidden="true">
                {videoIds.map(id => (
                    <YouTube
                        key={id}
                        videoId={id}
                        opts={{ height: '90', width: '160', playerVars: { autoplay: 0 } }}
                        onReady={(e) => setDurations(prev => ({ ...prev, [id]: formatDuration(e.target.getDuration()) }))}
                    />
                ))}
            </div>
        </div>
    )
}

export default DlpCards
